"use client";

import React, { useEffect, useMemo, useState } from "react";
import type { Product } from "@/types";
import { MAIN_COLORS, COLOR_SWATCH_HEX as COLOR_HEX } from "@/lib/colors";
import CategoryTreeFilter from "@/components/CategoryTreeFilter"; // MOW-CATEGORIEFILTER-OKT26
import {
  cardsOfProduct, categoryLabel, countCardsPerCategory, pruneTreeByCounts, rootedTree, toggleCategory,
  type CategoryNode,
} from "@/lib/category-filter"; // MOW-CATEGORIEFILTER-OKT26
import { useCategoryTree } from "@/lib/navigation-client"; // MOW-CATEGORIEFILTER-OKT26

import { DEFAULT_FILTERS, applyFilters, type ShopFilters } from "@/lib/shop-filters";
import { splitFit } from "@/lib/filter-query"; // MOW-PASVORM-NOV26
import { PRODUCT_PROPERTIES, productProperties, propertyLabel } from "@/lib/product-properties"; // MOW-EIGENSCHAPPEN-NOV26
// Verhuisd naar lib/shop-filters.ts (ook nodig op de server); hier opnieuw geëxporteerd zodat bestaande imports blijven werken.
export { DEFAULT_FILTERS, applyFilters };
export type { ShopFilters };

// >>> MOW-FILTERS-OKT26 3: één plek voor de teksten met een aantal
/** "1 product", "9 producten": voor de teller boven het raster, de mobiele balk en de paneelknop. */
export function formatProductCount(n: number): string {
  return `${n} ${n === 1 ? "product" : "producten"}`;
}
// <<< MOW-FILTERS-OKT26 3

// >>> MOW-FILTERS-OKT26 4: chips voor álle actieve filters (zoekterm, maat, merk, materiaal, kleur, prijs)
export interface ActiveFilterChip {
  key: string;
  /** Zichtbare tekst in de chip. */
  label: string;
  /** Tekst voor schermlezers op de verwijderknop. */
  ariaLabel: string;
  /** Geeft de filters terug zonder dit ene filter. */
  clear: (f: ShopFilters) => ShopFilters;
}

/**
 * Volgorde van Emily's filterlijst (maat, merk, materiaal, kleur, prijs), met de
 * zoekterm vooraan. De prijs telt als actief zodra een van beide grenzen van de
 * beginstand (DEFAULT_FILTERS: € 0 tot € 200) afwijkt; de duurste kaart is nu
 * € 89,99, dus de schuif loopt in de praktijk niet verder dan die 200.
 */
export function getActiveFilterChips(filters: ShopFilters, categoryLabels?: ReadonlyMap<string, string> | null): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];
  const add = (key: string, label: string, soort: string, clear: (f: ShopFilters) => ShopFilters) =>
    chips.push({ key, label, ariaLabel: `Verwijder filter ${soort}`, clear });

  if (filters.search) {
    add("zoek", `🔍 “${filters.search}”`, `Zoeken ${filters.search}`, (f) => ({ ...f, search: "" }));
  }
  // >>> MOW-CATEGORIEFILTER-OKT26: categorieën en Sale komen vóór maat, merk, ... (de volgorde van het paneel)
  for (const c of filters.categories ?? []) {
    const naam = categoryLabel(categoryLabels, c);
    add(`categorie:${c}`, `Categorie: ${naam}`, `Categorie ${naam}`, (f) => ({ ...f, categories: f.categories.filter((x) => x !== c) }));
  }
  if (filters.sale) {
    add("sale", "Sale", "Sale", (f) => ({ ...f, sale: false }));
  }
  // <<< MOW-CATEGORIEFILTER-OKT26
  for (const s of filters.sizes) {
    add(`maat:${s}`, `Maat: ${s}`, `Maat ${s}`, (f) => ({ ...f, sizes: f.sizes.filter((x) => x !== s) }));
  }
  for (const b of filters.brands) {
    add(`merk:${b}`, `🏷 ${b}`, `Merk ${b}`, (f) => ({ ...f, brands: f.brands.filter((x) => x !== b) }));
  }
  // MOW-PASVORM-NOV26: Emily's volgorde: maat, merk, model/pasvorm, materiaal, ...
  for (const f of filters.fits ?? []) {
    add(`pasvorm:${f}`, `Pasvorm: ${f}`, `Pasvorm ${f}`, (x) => ({ ...x, fits: (x.fits ?? []).filter((y) => y !== f) }));
  }
  for (const m of filters.materials) {
    add(`materiaal:${m}`, `Materiaal: ${m}`, `Materiaal ${m}`, (f) => ({ ...f, materials: f.materials.filter((x) => x !== m) }));
  }
  for (const k of filters.properties ?? []) {
    add(`eigenschap:${k}`, `Eigenschap: ${propertyLabel(k)}`, `Eigenschap ${propertyLabel(k)}`, (x) => ({ ...x, properties: (x.properties ?? []).filter((y) => y !== k) })); // MOW-EIGENSCHAPPEN-NOV26
  }
  for (const c of filters.colors) {
    add(`kleur:${c}`, `🎨 ${c}`, `Kleur ${c}`, (f) => ({ ...f, colors: f.colors.filter((x) => x !== c) }));
  }
  const vanafActief = filters.priceMin > DEFAULT_FILTERS.priceMin;
  const totActief = filters.priceMax < DEFAULT_FILTERS.priceMax;
  if (vanafActief || totActief) {
    const label =
      vanafActief && totActief
        ? `Prijs: € ${filters.priceMin} - € ${filters.priceMax}`
        : vanafActief
        ? `Prijs: vanaf € ${filters.priceMin}`
        : `Prijs: tot € ${filters.priceMax}`;
    add("prijs", label, label, (f) => ({ ...f, priceMin: DEFAULT_FILTERS.priceMin, priceMax: DEFAULT_FILTERS.priceMax }));
  }
  return chips;
}

/** De chip-rij boven het raster; zelfde opmaak (.filter-badge) als de eerdere losse blokken in de drie pagina's. */
export function ActiveFilterChips({ filters, onChange }: { filters: ShopFilters; onChange: (f: ShopFilters) => void }) {
  const { labels } = useCategoryTree(); // MOW-CATEGORIEFILTER-OKT26: leesbare namen voor de categoriechips
  const chips = getActiveFilterChips(filters, labels);
  if (chips.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "14px" }}>
      {chips.map((c) => (
        <span key={c.key} className="filter-badge">
          {c.label}
          <button type="button" onClick={() => onChange(c.clear(filters))} aria-label={c.ariaLabel}>✕</button>
        </span>
      ))}
    </div>
  );
}
// <<< MOW-FILTERS-OKT26 4

// Maatlabels lopen in dit assortiment door elkaar (letter-, cijfer-, kinder-,
// combi- en cm-maten in één lijst) — een universele volgorde is er niet.
// Wél een herkenbare: eerst letters (S→6XL), dan letter+cm-combi's (bv.
// "M (98)"), dan losse cijfers oplopend, dan "X/Y"-combimaten oplopend, dan
// "AxA cm"-maten, en tot slot alles wat niet in een patroon past.
const LETTER_SIZE_ORDER = ["xxs", "xs", "s", "m", "l", "xl", "xxl", "3xl", "4xl", "5xl", "6xl"];
function sizeSortKey(raw: string): [number, number, number, string] {
  const s = raw.trim();
  const lower = s.toLowerCase();

  const letterOnly = lower.match(/^(xxs|xs|s|m|l|xl|xxl|xxxl|[3-6]xl)$/);
  if (letterOnly) {
    const key = lower === "xxxl" ? "3xl" : lower;
    const idx = LETTER_SIZE_ORDER.indexOf(key);
    return [0, idx === -1 ? 999 : idx, 0, s];
  }

  const letterWithNumber = lower.match(/^(xxs|xs|s|m|l|xl|xxl|xxxl|[3-6]xl)\s*\((\d+)\)$/);
  if (letterWithNumber) return [1, parseInt(letterWithNumber[2], 10), 0, s];

  const combined = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (combined) return [3, parseInt(combined[1], 10), parseInt(combined[2], 10), s];

  const cm = lower.match(/^(\d+)\s*x\s*(\d+)\s*cm$/);
  if (cm) return [4, parseInt(cm[1], 10), parseInt(cm[2], 10), s];

  const plainNumber = s.match(/^\d+([.,]\d+)?$/);
  if (plainNumber) return [2, parseFloat(s.replace(",", ".")), 0, s];

  return [5, 0, 0, s];
}
function compareSizes(a: string, b: string): number {
  const ka = sizeSortKey(a);
  const kb = sizeSortKey(b);
  for (let i = 0; i < 3; i++) {
    if (ka[i] !== kb[i]) return (ka[i] as number) - (kb[i] as number);
  }
  return ka[3].localeCompare(kb[3]);
}

interface Props {
  products: Product[];
  filters: ShopFilters;
  onChange: (f: ShopFilters) => void;
  resultCount?: number;
  /** Sortering waarop de pagina opent (standaard "relevance"); "Alle filters wissen" keert daarnaar terug. */
  defaultSort?: string;
  // >>> MOW-CATEGORIEFILTER-OKT26
  /** Slug van de categorie waar deze pagina over gaat: de boom toont dan alleen wat eronder valt. Weglaten op /shop en merkpagina's: de hele boom. */
  categoryRoot?: string;
  /** Wat de boom toont zolang het menu nog niet binnen is (de blokken van de pagina), zodat er niets verspringt. */
  fallbackCategories?: ReadonlyArray<CategoryNode>;
  /** Is de lijst compleet? Pas dan kloppen de aantallen; ervoor staat er geen getal achter. */
  listComplete?: boolean;
  /** Is het laden van de volledige lijst mislukt? Dan tonen maat, merk, materiaal en kleur wat er is (geen eeuwige plaatshouder). */
  laadMislukt?: boolean;
  /** De sale-regel van de pagina (lib/product-sale.ts met de lopende acties); zonder deze functie is er geen Sale-keuze. */
  saleCheck?: (p: Product) => boolean;
  // <<< MOW-CATEGORIEFILTER-OKT26
}

const SORT_OPTIONS = [
  { value: "relevance", label: "Bestverkocht" },
  { value: "price-asc", label: "Prijs: laag → hoog" },
  { value: "price-desc", label: "Prijs: hoog → laag" },
  { value: "name-asc", label: "Naam A → Z" },
  { value: "newest", label: "Nieuwste eerst" },
];

function CollapsibleSection({
  title,
  count,
  children,
  defaultOpen = true,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="sidebar-section">
      <button
        className="sidebar-title sidebar-title-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{ width: "100%", background: "none", border: "none", cursor: "pointer", padding: 0 }}
      >
        <span>{title}</span>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {count !== undefined && count > 0 && (
            <span style={{ background: "var(--color-primary)", color: "#fff", borderRadius: "10px", fontSize: "0.65rem", padding: "1px 6px", fontWeight: 700 }}>
              {count}
            </span>
          )}
          <span style={{ fontSize: "0.8rem", color: "var(--color-gray-400)", transition: "transform 0.2s", display: "inline-block", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
            ▾
          </span>
        </span>
      </button>
      {open && <div style={{ marginTop: "10px" }}>{children}</div>}
    </div>
  );
}

// >>> MOW-FILTERPANEEL-NOV26: plaatshouder voor een blok dat op de volledige lijst wacht.
// Zolang alleen de eerste pagina er is, kennen maat, merk en kleur maar een deel van de opties (36 van de 95 maten);
// die lijst groeide na een paar seconden en schoof alles eronder omlaag. Nu staat er een rustig vlak van de
// uiteindelijke hoogte en komen de opties er in één keer voor in de plaats.
function FacetPlaceholder({ title, hoogte }: { title: string; hoogte: number }) {
  return (
    <div className="sidebar-section" aria-busy="true">
      <div className="sidebar-title sidebar-title-toggle" style={{ lineHeight: "normal" }}>
        <span>{title}</span>
      </div>
      {hoogte > 0 && (
        <div
          role="status"
          aria-label={`${title} laden`}
          style={{ marginTop: "10px", height: `${hoogte}px`, background: "var(--color-gray-100, #f3f3f3)", borderRadius: "4px" }}
        />
      )}
    </div>
  );
}
// <<< MOW-FILTERPANEEL-NOV26

/** Hoogte van de plaatshouder voor Pasvorm: zes keuzes onder elkaar; gemeten op /shop (sectie 237 px, minus kop en marges). */
const FIT_PLACEHOLDER_HEIGHT = 191;

function FilterContent({
  products, filters, onChange, globalMin, globalMax,
  brandCounts, availableColors, materialCounts, sizeCounts, fitCounts, propertyCounts, activeFilterCount, defaultSort,
  categoryNodes, categoryTree, categoryCounts, saleCount, facetsKlaar,
}: {
  products: Product[]; filters: ShopFilters; onChange: (f: ShopFilters) => void;
  globalMin: number; globalMax: number; defaultSort?: string;
  brandCounts: [string, number][]; availableColors: string[]; materialCounts: [string, number][]; sizeCounts: [string, number][]; fitCounts: [string, number][]; propertyCounts: [string, number][]; activeFilterCount: number;
  // MOW-CATEGORIEFILTER-OKT26
  categoryNodes: ReadonlyArray<CategoryNode>; categoryTree: ReadonlyArray<CategoryNode> | null;
  categoryCounts: ReadonlyMap<string, number> | null; saleCount: number | null;
  facetsKlaar: boolean; // MOW-FILTERPANEEL-NOV26: klopt de lijst voor maat/merk/materiaal/kleur al?
}) {
  const set = (patch: Partial<ShopFilters>) => onChange({ ...filters, ...patch });

  const toggleBrand = (brand: string) =>
    set({ brands: filters.brands.includes(brand) ? filters.brands.filter((b) => b !== brand) : [...filters.brands, brand] });
  const toggleColor = (color: string) =>
    set({ colors: filters.colors.includes(color) ? filters.colors.filter((c) => c !== color) : [...filters.colors, color] });
  const toggleMaterial = (m: string) =>
    set({ materials: filters.materials.includes(m) ? filters.materials.filter((x) => x !== m) : [...filters.materials, m] });
  const toggleFit = (f: string) =>
    set({ fits: (filters.fits ?? []).includes(f) ? (filters.fits ?? []).filter((x) => x !== f) : [...(filters.fits ?? []), f] }); // MOW-PASVORM-NOV26
  const toggleProperty = (k: string) =>
    set({ properties: (filters.properties ?? []).includes(k) ? (filters.properties ?? []).filter((x) => x !== k) : [...(filters.properties ?? []), k] }); // MOW-EIGENSCHAPPEN-NOV26
  const toggleSize = (s: string) =>
    set({ sizes: filters.sizes.includes(s) ? filters.sizes.filter((x) => x !== s) : [...filters.sizes, s] });

  return (
    <>
      {products.length > 0 && (
        <>
          {/* MOW-FILTERPANEEL-NOV26: de knop staat er altijd (uitgegrijsd zonder filters). Verscheen hij pas bij het eerste
              vinkje, dan schoof de hele lijst 64 px omlaag en raakte de volgende tik het verkeerde vakje. */}
          <div className="sidebar-section" style={{ paddingBottom: "12px" }}>
            <button
              type="button"
              disabled={activeFilterCount === 0}
              onClick={() => onChange({ ...DEFAULT_FILTERS, sort: defaultSort ?? DEFAULT_FILTERS.sort, priceMin: globalMin, priceMax: globalMax })}
              style={{
                width: "100%", padding: "8px 12px", background: "var(--color-primary)", color: "#fff", border: "none", borderRadius: "4px",
                cursor: activeFilterCount === 0 ? "default" : "pointer", fontSize: "0.8rem", fontWeight: 600,
                opacity: activeFilterCount === 0 ? 0.4 : 1,
              }}
            >
              {activeFilterCount > 0 ? `Alle filters wissen (${activeFilterCount})` : "Alle filters wissen"}
            </button>
          </div>

          {/* >>> MOW-CATEGORIEFILTER-OKT26: ── CATEGORIEËN ── bovenaan, voor al het andere (N9), en daaronder Sale */}
          {categoryNodes.length > 0 && (
            <CollapsibleSection title="Categorieën" count={filters.categories.length} defaultOpen={true}>
              <CategoryTreeFilter
                nodes={categoryNodes}
                selected={filters.categories}
                counts={categoryCounts}
                tree={categoryTree}
                onToggle={(slug) => set({ categories: toggleCategory(filters.categories, slug, categoryTree) })}
              />
            </CollapsibleSection>
          )}
          {saleCount !== null && (
            <div className="sidebar-section">
              <label className="filter-sale-row">
                <input
                  type="checkbox"
                  className="cat-tree-checkbox"
                  checked={filters.sale}
                  onChange={() => set({ sale: !filters.sale })}
                />
                <span className="cat-tree-name">Sale</span>
                {(filters.sale || saleCount > 0) && <span className="cat-tree-count">({saleCount})</span>}
              </label>
            </div>
          )}
          {/* <<< MOW-CATEGORIEFILTER-OKT26 */}

          {/* ── ZOEKEN ── */}
          <CollapsibleSection title="Zoeken" defaultOpen={true}>
            <div style={{ position: "relative" }}>
              {/* MOW-FILTERS-OKT26 5: label voor schermlezers; focusring via .filter-search-input:focus-visible (outline: none is weg) */}
              <input
                type="search" value={filters.search}
                onChange={(e) => set({ search: e.target.value })}
                placeholder="Zoek op naam…"
                aria-label="Zoek op naam"
                className="filter-search-input"
                style={{ width: "100%", padding: "9px 34px 9px 11px", border: "1px solid var(--color-gray-200)", borderRadius: "4px", fontSize: "0.85rem", boxSizing: "border-box" }}
              />
              {filters.search && (
                // MOW-FILTERS-OKT26 5: kleur was #aaa (2,3:1 op wit), nu het token #767676 (4,5:1)
                <button onClick={() => set({ search: "" })}
                  style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--color-gray-400)", fontSize: "1rem" }}
                  aria-label="Zoekterm wissen">✕</button>
              )}
            </div>
          </CollapsibleSection>

          {/* ── SORTEREN ── */}
          <CollapsibleSection title="Sorteren" defaultOpen={true}>
            <select value={filters.sort} onChange={(e) => set({ sort: e.target.value })}
              aria-label="Sorteren"
              style={{ width: "100%", padding: "9px 11px", border: "1px solid var(--color-gray-200)", borderRadius: "4px", fontSize: "0.85rem", background: "#fff", cursor: "pointer" }}>
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </CollapsibleSection>

          {/* ── MAAT ── */}
          {!facetsKlaar ? (
            <FacetPlaceholder title="Maat" hoogte={220} />
          ) : sizeCounts.length > 0 && (
            <CollapsibleSection title="Maat" count={filters.sizes.length} defaultOpen={true}>
              {/* MOW-FILTERPANEEL-NOV26: net als Merk een eigen scrollvlak; 95 maten maakten het paneel meters hoog en laat groeien */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", maxHeight: "220px", overflowY: "auto", padding: "3px" }}>
                {sizeCounts.map(([size, count]) => {
                  const active = filters.sizes.includes(size);
                  return (
                    <button
                      key={size}
                      type="button"
                      onClick={() => toggleSize(size)}
                      aria-pressed={active}
                      title={`${count} product${count !== 1 ? "en" : ""}`}
                      style={{
                        padding: "6px 12px",
                        border: active ? "2px solid var(--color-primary)" : "1px solid var(--color-gray-200)",
                        background: active ? "var(--color-primary-light, #fdf6f6)" : "#fff",
                        color: active ? "var(--color-primary)" : "var(--color-gray-600)",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        fontWeight: active ? 600 : 400,
                        cursor: "pointer",
                      }}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </CollapsibleSection>
          )}

          {/* ── MERK ── */}
          {!facetsKlaar ? (
            <FacetPlaceholder title="Merk" hoogte={200} />
          ) : brandCounts.length > 0 && (
            <CollapsibleSection title="Merk" count={filters.brands.length} defaultOpen={true}>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: "200px", overflowY: "auto" }}>
                {brandCounts.map(([brand, count]) => {
                  const active = filters.brands.includes(brand);
                  return (
                    <li key={brand}>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 0", cursor: "pointer", fontSize: "0.85rem", color: active ? "var(--color-primary)" : "var(--color-gray-600)", fontWeight: active ? 600 : 400 }}>
                        <input type="checkbox" checked={active} onChange={() => toggleBrand(brand)} style={{ accentColor: "var(--color-primary)", width: "14px", height: "14px" }} />
                        <span style={{ flex: 1 }}>{brand}</span>
                        <span style={{ fontSize: "0.72rem", color: "var(--color-gray-400)" }}>({count})</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </CollapsibleSection>
          )}

          {/* ── PASVORM (model) ── MOW-PASVORM-NOV26: tussen Merk en Materiaal, zoals Emily's volgorde */}
          {!facetsKlaar ? (
            <FacetPlaceholder title="Pasvorm" hoogte={FIT_PLACEHOLDER_HEIGHT} />
          ) : fitCounts.length > 0 && (
            <CollapsibleSection title="Pasvorm" count={(filters.fits ?? []).length} defaultOpen={true}>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {fitCounts.map(([fit, count]) => {
                  const active = (filters.fits ?? []).includes(fit);
                  return (
                    <li key={fit}>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 0", cursor: "pointer", fontSize: "0.85rem", color: active ? "var(--color-primary)" : "var(--color-gray-600)", fontWeight: active ? 600 : 400 }}>
                        <input type="checkbox" checked={active} onChange={() => toggleFit(fit)} style={{ accentColor: "var(--color-primary)", width: "14px", height: "14px" }} />
                        <span style={{ flex: 1 }}>{fit}</span>
                        <span style={{ fontSize: "0.72rem", color: "var(--color-gray-400)" }}>({count})</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </CollapsibleSection>
          )}

          {/* ── MATERIAAL ── */}
          {!facetsKlaar ? (
            <FacetPlaceholder title="Materiaal" hoogte={0} />
          ) : materialCounts.length > 0 && (
            <CollapsibleSection title="Materiaal" count={filters.materials.length} defaultOpen={false}>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: "200px", overflowY: "auto" }}>
                {materialCounts.map(([mat, count]) => {
                  const active = filters.materials.includes(mat);
                  return (
                    <li key={mat}>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 0", cursor: "pointer", fontSize: "0.85rem", color: active ? "var(--color-primary)" : "var(--color-gray-600)", fontWeight: active ? 600 : 400 }}>
                        <input type="checkbox" checked={active} onChange={() => toggleMaterial(mat)} style={{ accentColor: "var(--color-primary)", width: "14px", height: "14px" }} />
                        <span style={{ flex: 1 }}>{mat}</span>
                        <span style={{ fontSize: "0.72rem", color: "var(--color-gray-400)" }}>({count})</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </CollapsibleSection>
          )}
          {/* ── EIGENSCHAPPEN ── MOW-EIGENSCHAPPEN-NOV26: een korte, gekozen lijst (lib/product-properties.ts), na Materiaal en vóór Kleur */}
          {!facetsKlaar ? (
            <FacetPlaceholder title="Eigenschappen" hoogte={FIT_PLACEHOLDER_HEIGHT} />
          ) : propertyCounts.length > 0 && (
            <CollapsibleSection title="Eigenschappen" count={(filters.properties ?? []).length} defaultOpen={true}>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {propertyCounts.map(([key, count]) => {
                  const active = (filters.properties ?? []).includes(key);
                  return (
                    <li key={key}>
                      <label style={{ display: "flex", alignItems: "center", gap: "8px", padding: "5px 0", cursor: "pointer", fontSize: "0.85rem", color: active ? "var(--color-primary)" : "var(--color-gray-600)", fontWeight: active ? 600 : 400 }}>
                        <input type="checkbox" checked={active} onChange={() => toggleProperty(key)} style={{ accentColor: "var(--color-primary)", width: "14px", height: "14px" }} />
                        <span style={{ flex: 1 }}>{propertyLabel(key)}</span>
                        <span style={{ fontSize: "0.72rem", color: "var(--color-gray-400)" }}>({count})</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </CollapsibleSection>
          )}
          {/* ── KLEUR ── */}
          {!facetsKlaar ? (
            <FacetPlaceholder title="Kleur" hoogte={106} />
          ) : availableColors.length > 0 && (
            <CollapsibleSection title="Kleur" count={filters.colors.length} defaultOpen={true}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "2px" }}>
                {availableColors.map((color) => {
                  const active = filters.colors.includes(color);
                  const bg = COLOR_HEX[color] || "#ccc";
                  const isGradient = bg.includes("gradient");
                  return (
                    // MOW-FILTERS-OKT26 5: outline: "none" (inline) is weg; de focusring staat in .filter-swatch:focus-visible
                    <button key={color} title={color} onClick={() => toggleColor(color)} aria-label={color} aria-pressed={active}
                      className="filter-swatch"
                      style={{ width: "30px", height: "30px", borderRadius: "50%", border: active ? "2.5px solid var(--color-primary)" : "2px solid #ddd", background: isGradient ? bg : undefined, backgroundColor: isGradient ? undefined : bg, cursor: "pointer", padding: 0, boxShadow: active ? "0 0 0 2px #fff, 0 0 0 4px var(--color-primary)" : color === "Wit" ? "0 0 0 1px #ddd" : undefined, transform: active ? "scale(1.15)" : "scale(1)", transition: "all 0.15s ease" }}
                    />
                  );
                })}
              </div>
              {filters.colors.length > 0 && (
                <div style={{ marginTop: "8px", fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
                  {filters.colors.join(", ")}
                  <button onClick={() => set({ colors: [] })} style={{ marginLeft: "6px", background: "none", border: "none", cursor: "pointer", color: "var(--color-primary)", fontSize: "0.75rem" }}>✕ wissen</button>
                </div>
              )}
            </CollapsibleSection>
          )}

          {/* ── PRIJS ── */}
          <CollapsibleSection title="Prijs" defaultOpen={true}>
            <div style={{ padding: "0 2px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--color-secondary)" }}>€ {filters.priceMin}</span>
                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--color-secondary)" }}>€ {filters.priceMax}</span>
              </div>
              <div style={{ position: "relative", height: "32px" }}>
                {/* MOW-FILTERS-OKT26 5: namen voor schermlezers op de twee schuifknoppen */}
                <input type="range" min={globalMin} max={globalMax} value={filters.priceMin}
                  onChange={(e) => { const v = Number(e.target.value); if (v < filters.priceMax) set({ priceMin: v }); }}
                  aria-label="Minimumprijs" aria-valuetext={`€ ${filters.priceMin}`}
                  className="price-slider price-slider-min"
                  style={{ "--pct": `${((filters.priceMin - globalMin) / (globalMax - globalMin)) * 100}%` } as React.CSSProperties} />
                <input type="range" min={globalMin} max={globalMax} value={filters.priceMax}
                  onChange={(e) => { const v = Number(e.target.value); if (v > filters.priceMin) set({ priceMax: v }); }}
                  aria-label="Maximumprijs" aria-valuetext={`€ ${filters.priceMax}`}
                  className="price-slider price-slider-max"
                  style={{ "--pct": `${((filters.priceMax - globalMin) / (globalMax - globalMin)) * 100}%` } as React.CSSProperties} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", fontSize: "0.72rem", color: "var(--color-gray-400)" }}>
                <span>Min: € {globalMin}</span><span>Max: € {globalMax}</span>
              </div>
            </div>
          </CollapsibleSection>

        </>
      )}

    </>
  );
}

export default function ShopSidebar({ products, filters, onChange, resultCount, defaultSort, categoryRoot, fallbackCategories, listComplete = true, laadMislukt = false, saleCheck }: Props) {
  const facetsKlaar = listComplete || laadMislukt; // MOW-FILTERPANEEL-NOV26
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { tree: categoryTree } = useCategoryTree(); // MOW-CATEGORIEFILTER-OKT26

  // Sluit drawer bij Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setDrawerOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [drawerOpen]);

  // Vergrendel body-scroll als drawer open is
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  // Facet-tellingen: elke facet past alle ACTIEVE filters toe behalve zijn
  // eigen selectie (standaard faceted-search patroon). Zo klopt bv. de
  // merk-telling met de actieve zoekterm/prijs/kleur/materiaal, terwijl je
  // per merk nog ziet hoeveel resultaten dát merk zou toevoegen.
  const brandCounts = useMemo(() => {
    const base = applyFilters(products, { ...filters, brands: [] });
    const map: Record<string, number> = {};
    base.forEach((p) => { if (p.brand) map[p.brand] = (map[p.brand] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [products, filters]);

  const availableColors = useMemo(() => {
    const base = applyFilters(products, { ...filters, colors: [] });
    const set = new Set<string>();
    base.forEach((p) => {
      (p.mainColors || []).forEach((c: string) => { if (c) set.add(c); });
      if (p.mainColor) set.add(p.mainColor);
    });
    return MAIN_COLORS.filter((c) => set.has(c));
  }, [products, filters]);

  // Materialen: hoofdletter-varianten (bv. "100% katoen"/"100% Katoen") worden
  // samengevoegd op kleine letters; het meest voorkomende label wordt getoond.
  const materialCounts = useMemo<[string, number][]>(() => {
    const base = applyFilters(products, { ...filters, materials: [] });
    const byKey: Record<string, { count: number; labels: Record<string, number> }> = {};
    base.forEach((p) => {
      for (const raw of (p.materials || [])) {
        const label = String(raw).trim();
        if (!label) continue;
        const key = label.toLowerCase();
        const e = (byKey[key] = byKey[key] || { count: 0, labels: {} });
        e.count++;
        e.labels[label] = (e.labels[label] || 0) + 1;
      }
    });
    return Object.values(byKey)
      .map((e) => [Object.entries(e.labels).sort((a, b) => b[1] - a[1])[0][0], e.count] as [string, number])
      .sort((a, b) => b[1] - a[1]);
  }, [products, filters]);

  // Maten: op logische maatvolgorde (zie compareSizes hierboven) i.p.v. op
  // populariteit — die laatste liet letter-/cijfer-/kindermaten willekeurig
  // door elkaar staan, onoverzichtelijk bij zoveel verschillende maattypes
  // in één assortiment.
  const sizeCounts = useMemo<[string, number][]>(() => {
    const base = applyFilters(products, { ...filters, sizes: [] });
    const map: Record<string, number> = {};
    base.forEach((p) => {
      for (const raw of (p.sizes || [])) {
        const label = String(raw).trim();
        if (label) map[label] = (map[label] || 0) + 1;
      }
    });
    return Object.entries(map).sort((a, b) => compareSizes(a[0], b[0]));
  }, [products, filters]);

  // Pasvormen: een product kan er twee hebben ("Aansluitend, Normaal") en telt dan bij beide mee. MOW-PASVORM-NOV26
  const fitCounts = useMemo<[string, number][]>(() => {
    const base = applyFilters(products, { ...filters, fits: [] });
    const byKey: Record<string, { label: string; count: number }> = {};
    base.forEach((p) => {
      const seen = new Set<string>();
      for (const f of splitFit(p.fit)) {
        const key = f.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        const e = (byKey[key] = byKey[key] || { label: f, count: 0 });
        e.count++;
      }
    });
    return Object.values(byKey).map((e) => [e.label, e.count] as [string, number]).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "nl"));
  }, [products, filters]);

  // Eigenschappen: alleen die met resultaat (of al gekozen), in de volgorde van de lijst. MOW-EIGENSCHAPPEN-NOV26
  const propertyCounts = useMemo<[string, number][]>(() => {
    const base = applyFilters(products, { ...filters, properties: [] });
    const counts: Record<string, number> = {};
    base.forEach((p) => { for (const k of productProperties(p)) counts[k] = (counts[k] || 0) + 1; });
    const gekozen = filters.properties ?? [];
    return PRODUCT_PROPERTIES.filter((d) => (counts[d.key] || 0) > 0 || gekozen.includes(d.key)).map((d) => [d.key, counts[d.key] || 0] as [string, number]);
  }, [products, filters]);

  const { globalMin, globalMax } = useMemo(() => {
    const base = applyFilters(products, { ...filters, priceMin: -Infinity, priceMax: Infinity });
    const prices = base.map((p) => p.salePrice ?? p.price).filter(Boolean);
    return { globalMin: Math.floor(Math.min(...prices, 0)), globalMax: Math.ceil(Math.max(...prices, 200)) };
  }, [products, filters]);

  // >>> MOW-CATEGORIEFILTER-OKT26
  // Wat de boom toont: de lagen onder de categorie van deze pagina, of op /shop de hele boom uit het menu. Is het
  // menu er nog niet, of staat de categorie er niet in (een diepe categorie), dan de blokken van de pagina zelf.
  const baseNodes = useMemo<ReadonlyArray<CategoryNode>>(() => {
    const fallback = fallbackCategories ?? [];
    if (!categoryTree) return fallback;
    if (categoryRoot) return rootedTree(categoryTree, categoryRoot) ?? fallback;
    return categoryTree;
  }, [categoryTree, categoryRoot, fallbackCategories]);

  // Aantal kaarten per categorie, zoals de andere facetten: met alle andere keuzes toegepast, behalve de categorieën zelf.
  const categoryCounts = useMemo(() => {
    if (!listComplete) return null;
    let base = applyFilters(products, { ...filters, categories: [] });
    if (filters.sale && saleCheck) base = base.filter(saleCheck);
    return countCardsPerCategory(base);
  }, [products, filters, listComplete, saleCheck]);

  const categoryNodes = useMemo<ReadonlyArray<CategoryNode>>(
    () => (categoryCounts ? pruneTreeByCounts(baseNodes, categoryCounts, filters.categories) : baseNodes),
    [baseNodes, categoryCounts, filters.categories]
  );

  // Aantal sale-kaarten binnen de rest van de keuze (categorieën meegeteld, Sale zelf niet).
  const saleCount = useMemo(() => {
    if (!saleCheck) return null;
    if (!listComplete) return 0;
    return applyFilters(products, filters).filter(saleCheck).reduce((som, p) => som + cardsOfProduct(p), 0);
  }, [products, filters, listComplete, saleCheck]);
  // <<< MOW-CATEGORIEFILTER-OKT26

  const activeFilterCount =
    (filters.search ? 1 : 0) + filters.brands.length + filters.colors.length + filters.materials.length + (filters.fits?.length ?? 0) + (filters.properties?.length ?? 0) + filters.sizes.length +
    (filters.priceMin > globalMin || filters.priceMax < globalMax ? 1 : 0) +
    (filters.categories?.length ?? 0) + (filters.sale ? 1 : 0); // MOW-CATEGORIEFILTER-OKT26

  const sharedProps = {
    products, filters, onChange, globalMin, globalMax, brandCounts, availableColors, materialCounts, sizeCounts, fitCounts, propertyCounts, activeFilterCount, defaultSort,
    categoryNodes, categoryTree, categoryCounts, saleCount, // MOW-CATEGORIEFILTER-OKT26
    facetsKlaar, // MOW-FILTERPANEEL-NOV26
  };

  return (
    <>
      {/* ── DESKTOP: vaste sidebar links ── */}
      <aside className="shop-sidebar shop-sidebar-desktop">
        <FilterContent {...sharedProps} />
      </aside>

      {/* ── MOBIEL: sticky balk met "Filteren" + "Sorteren" ── */}
      <div className="shop-filter-bar-mobile">
        <button
          className="shop-filter-btn"
          onClick={() => setDrawerOpen(true)}
          aria-expanded={drawerOpen}
          aria-label="Filters openen"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" /><line x1="11" y1="18" x2="13" y2="18" />
          </svg>
          Filteren
          {activeFilterCount > 0 && (
            <span style={{ background: "var(--color-primary)", color: "#fff", borderRadius: "10px", fontSize: "0.65rem", padding: "1px 7px", fontWeight: 700, marginLeft: "2px" }}>
              {activeFilterCount}
            </span>
          )}
        </button>

        <select
          value={filters.sort}
          onChange={(e) => onChange({ ...filters, sort: e.target.value })}
          className="shop-sort-select-mobile"
          aria-label="Sorteren"
        >
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        {resultCount !== undefined && (
          <span style={{ fontSize: "0.78rem", color: "var(--color-gray-400)", marginLeft: "auto" }}>
            {formatProductCount(resultCount)}
          </span>
        )}
      </div>

      {/* ── MOBIEL: slide-up drawer ── */}
      {drawerOpen && (
        <>
          {/* Overlay */}
          <div
            onClick={() => setDrawerOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, touchAction: "none" }}
            aria-hidden="true"
          />
          {/* Drawer */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Filters"
            style={{
              position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 201,
              background: "#fff",
              borderRadius: "20px 20px 0 0",
              maxHeight: "88dvh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
              animation: "slideUpDrawer 0.3s cubic-bezier(0.4,0,0.2,1)",
            }}
          >
            {/* Drawer header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px 12px", borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
              {/* Drag handle */}
              <div style={{ position: "absolute", top: "10px", left: "50%", transform: "translateX(-50%)", width: "36px", height: "4px", background: "#ddd", borderRadius: "2px" }} />
              <span style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>
                Filters {activeFilterCount > 0 && <span style={{ color: "var(--color-primary)", fontWeight: 400, fontSize: "0.85rem" }}>({activeFilterCount} actief)</span>}
              </span>
              <button onClick={() => setDrawerOpen(false)} aria-label="Sluiten"
                style={{ border: "none", background: "none", cursor: "pointer", fontSize: "1.3rem", color: "#555", padding: "4px 8px", lineHeight: 1 }}>
                ✕
              </button>
            </div>

            {/* Scrollbare inhoud */}
            <div style={{ overflowY: "auto", flex: 1, padding: "0 4px" }}>
              <FilterContent {...sharedProps} />
            </div>

            {/* Vaste "Bekijk resultaten" knop */}
            <div style={{ padding: "12px 16px 24px", borderTop: "1px solid #f0f0f0", flexShrink: 0 }}>
              <button
                onClick={() => setDrawerOpen(false)}
                style={{ width: "100%", padding: "14px", background: "var(--color-primary)", color: "#fff", border: "none", borderRadius: "6px", fontSize: "0.95rem", fontWeight: 700, cursor: "pointer", letterSpacing: "0.03em" }}
              >
                {/* MOW-FILTERS-OKT26 3: "Toon N producten" (kaarten, zoals het raster ze toont) op alle drie de pagina's */}
                {resultCount !== undefined ? `Toon ${formatProductCount(resultCount)}` : "Filters toepassen"}
              </button>
            </div>
          </div>

          <style>{`
            @keyframes slideUpDrawer {
              from { transform: translateY(100%); }
              to   { transform: translateY(0); }
            }
          `}</style>
        </>
      )}
    </>
  );
}
